import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Typography, Alert, Button, Space, Divider, Table, Input, DatePicker, Spin, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Slot, CheckExistsResponse } from '../../../Modules/Slot/Model/Slot';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../Modules/Presentation/hooks';
import { checkSlotExists } from '../../../Modules/Slot/Presentation/actions';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import { selectUser } from '../../../Modules/User/Presentation/selectors';

const { Paragraph, Text } = Typography;
const { Option } = Select;

interface Props {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onSubmit: (slots: Partial<Slot>[]) => Promise<void> | void;
}

type RawRow = Record<string, any>;

// 엑셀 헤더 매핑 (일괄 수정용)
const headerMap: Record<string, string[]> = {
  slotId: ['슬롯 번호', '슬롯번호', 'slotId', 'slot_id', 'id'],
  sellerId: ['셀러(Id)', '셀러(id)', '셀러', '판매자', 'seller_id', 'sellerId', '아이디'],
  main_keyword: ['메인키워드', '메인 키워드', 'main_keyword'],
  word_keyword: ['작업키워드', '작업 키워드', 'word_keyword'],
  mid_single: ['단일 MID', '단일MID', 'mid_single'],
  mid_compare: ['가격비교 MID', '가격비교MID', 'mid_compare'],
  product_url: ['상품 URL', '상품URL', 'product_url'],
  compare_url: ['가격비교 URL', '가격비교URL', 'compare_url'],
  memo: ['메모', 'memo'],
  start_date: ['시작일', 'start_date', 'start'],
  end_date: ['종료일', 'end_date', 'end'],
};

interface RowDraft extends Partial<Slot> {
  key: number;
  isExisting?: boolean;
  isValidating?: boolean;
  validationError?: string;
}

const SELLER_HEADER = '셀러(Id)';

const ExcelBatchUpdateModal: React.FC<Props> = ({ open, file, onClose, onSubmit }) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const accounts = useAppSelector(selectAccounts);
  const sellers = useMemo(
    () => accounts.filter(a => a.grade === 'SELLER').sort((a, b) => a.username.localeCompare(b.username)),
    [accounts]
  );

  const isAdmin = useMemo(() => currentUser?.grade === 'ADMIN', [currentUser]);
  const canEditDates = useMemo(
    () => isAdmin && !currentUser?.is_limited_partner_admin,
    [isAdmin, currentUser?.is_limited_partner_admin],
  );

  const [rawRows, setRawRows] = useState<RawRow[]>([]);
  const [rows, setRows] = useState<RowDraft[]>([]);
  const [bulkSellerId, setBulkSellerId] = useState<number | undefined>(undefined);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 모바일 판별
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 엑셀 파일 읽기
  useEffect(() => {
    if (!open || !file) return;
    setParseError(null);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<RawRow>(ws, { defval: '' });
        setRawRows(rows);
      } catch (err) {
        setParseError('엑셀 파일을 읽는 중 오류가 발생했습니다.');
      }
    };
    reader.onerror = () => {
      setParseError('엑셀 파일을 읽을 수 없습니다.');
    };
    reader.readAsArrayBuffer(file);
  }, [open, file]);

  // 셀러 ID 확인 로직
  const resolveSellerId = useCallback((value: any): number | undefined => {
    if (value == null || value === '') return undefined;

    // 숫자면 그대로 id로 처리
    if (typeof value === 'number' && Number.isFinite(value)) return Number(value);

    const raw = String(value).trim();
    if (!raw) return undefined;

    // "123" 형태면 id로 처리
    if (/^\d+$/.test(raw)) return Number(raw);

    // "username (name)" / "username" 모두 지원
    const usernameCandidate = raw.split('(')[0].trim().split(/\s+/)[0].trim();
    const lower = usernameCandidate.toLowerCase();
    const matched = sellers.find(s => s.username.toLowerCase() === lower);
    return matched?.id;
  }, [sellers]);

  // 엑셀 날짜 변환
  const toDateString = (value: any): string => {
    if (value == null || value === '') return '';
    if (typeof value === 'number') {
      const date = new Date(Math.round((value - 25569) * 86400 * 1000));
      return dayjs(date).format('YYYY-MM-DD');
    }
    const d = dayjs(value);
    return d.isValid() ? d.format('YYYY-MM-DD') : String(value);
  };

  // 초기 데이터 매핑 및 존재 여부 확인
  useEffect(() => {
    if (rawRows.length === 0) return;

    const detectedHeaders = new Set<string>();
    rawRows.forEach(r => Object.keys(r).forEach(k => detectedHeaders.add(String(k))));
    const headers = Array.from(detectedHeaders);

    const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();
    
    const findHeader = (field: string) => {
      const aliases = headerMap[field].map(norm);
      return headers.find(h => aliases.includes(norm(h)) || aliases.some(a => norm(h).includes(a)));
    };

    const initialRows = rawRows.map((r, idx) => {
      const getVal = (field: string) => {
        const h = findHeader(field);
        return h ? String(r[h]).trim() : '';
      };

      const slotIdRaw = getVal('slotId');
      const slotId = parseInt(slotIdRaw);

      const sellerRaw = r[SELLER_HEADER] ?? r['셀러(id)'] ?? getVal('sellerId');

      return {
        key: idx,
        slotId: isNaN(slotId) ? undefined : slotId,
        sellerId: resolveSellerId(sellerRaw),
        main_keyword: getVal('main_keyword'),
        word_keyword: getVal('word_keyword'),
        mid_single: getVal('mid_single'),
        mid_compare: getVal('mid_compare'),
        product_url: getVal('product_url'),
        compare_url: getVal('compare_url'),
        memo: getVal('memo'),
        start_date: toDateString(getVal('start_date')),
        end_date: toDateString(getVal('end_date')),
      } as RowDraft;
    });

    setRows(initialRows);
    validateSlotIds(initialRows);
    setBulkSellerId(undefined);
  }, [rawRows, resolveSellerId]);

  const validateSlotIds = async (targetRows: RowDraft[]) => {
    const ids = targetRows.map(r => r.slotId).filter((id): id is number => id !== undefined);
    if (ids.length === 0) return;

    setIsValidating(true);
    try {
      const res = await dispatch(checkSlotExists({ ids })).unwrap();
      const existingSet = new Set(res.existing_ids);

      setRows(prev => prev.map(r => {
        if (r.slotId === undefined) return { ...r, isExisting: false, validationError: '슬롯 번호를 입력해주세요.' };
        const exists = existingSet.has(r.slotId);
        return {
          ...r,
          isExisting: exists,
          validationError: exists ? undefined : '존재하지 않거나 권한이 없는 슬롯입니다.'
        };
      }));
    } catch (err) {
      setParseError('슬롯 확인 중 오류가 발생했습니다.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleRowChange = (key: number, field: keyof RowDraft, value: any) => {
    setRows(prev => prev.map(r => {
      if (r.key === key) {
        const updated = { ...r, [field]: value };
        if (field === 'slotId') {
          // slotId가 변경되면 다시 유효성 검사 필요
          updated.isExisting = undefined;
          updated.validationError = undefined;
        }
        return updated;
      }
      return r;
    }));
  };

  const getRowErrors = useCallback((r: RowDraft): string[] => {
    const errs: string[] = [];
    if (!r.slotId) errs.push('슬롯 번호 미입력');
    if (r.isExisting === false) errs.push('잘못된 슬롯 번호');
    if (!r.sellerId) errs.push('셀러 미선택');
    
    if (r.start_date && r.end_date) {
      const s = dayjs(r.start_date);
      const e = dayjs(r.end_date);
      if (s.isValid() && e.isValid() && e.isBefore(s)) {
        errs.push('종료일은 시작일 이후여야 합니다.');
      }
    }

    const urlRegex = /^(http|https):\/\/[^ "]+$/;
    if (r.product_url && !urlRegex.test(r.product_url)) {
      errs.push('상품 URL 형식이 올바르지 않습니다.');
    }
    if (r.compare_url && !urlRegex.test(r.compare_url)) {
      errs.push('가격비교 URL 형식이 올바르지 않습니다.');
    }

    return errs;
  }, []);

  const canSubmit = useMemo(() => 
    rows.length > 0 && !isValidating && rows.every(r => r.isExisting === true && getRowErrors(r).length === 0),
    [rows, isValidating, getRowErrors]
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      const updates = rows
        .map(({ key, isExisting, isValidating, validationError, ...rest }) => {
          if (!canEditDates) {
            const { start_date, end_date, ...withoutDates } = rest;
            return withoutDates;
          }
          return rest;
        })
        .filter((slot) => {
          const { slotId, ...updatable } = slot;
          return Object.keys(updatable).length > 0;
        });

      if (updates.length === 0) return;
      await onSubmit(updates as Partial<Slot>[]);
      onClose();
    } catch (err) {
      // 에러 처리는 부모에서
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnsType<RowDraft> = [
    {
      title: '슬롯 번호',
      dataIndex: 'slotId',
      width: 120,
      fixed: 'left',
      render: (val, r) => (
        <Input
            value={val}
            disabled={r.isExisting === true}
            status={r.validationError ? 'error' : undefined}
            onChange={e => {
              const v = parseInt(e.target.value);
              handleRowChange(r.key, 'slotId', isNaN(v) ? undefined : v);
            }}
          />
      )
    },
    {
      title: '셀러',
      dataIndex: 'sellerId',
      width: 200,
      render: (val, r) => (
        <Select
          value={val}
          style={{ width: '100%' }}
          placeholder="셀러 선택"
          showSearch
          optionFilterProp="label"
          optionLabelProp="label"
          onChange={v => handleRowChange(r.key, 'sellerId', v)}
        >
          {sellers.map(s => (
            <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
              {s.username} ({s.name})
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: '메인 키워드',
      dataIndex: 'main_keyword',
      width: 150,
      render: (val, r) => (
        <Input value={val} onChange={e => handleRowChange(r.key, 'main_keyword', e.target.value)} />
      )
    },
    {
      title: '단일 MID',
      dataIndex: 'mid_single',
      width: 150,
      render: (val, r) => (
        <Input value={val} onChange={e => handleRowChange(r.key, 'mid_single', e.target.value)} />
      )
    },
    {
      title: '가격비교 MID',
      dataIndex: 'mid_compare',
      width: 150,
      render: (val, r) => (
        <Input value={val} onChange={e => handleRowChange(r.key, 'mid_compare', e.target.value)} />
      )
    },
    {
      title: '상품 URL',
      dataIndex: 'product_url',
      width: 250,
      render: (val, r) => {
        const urlRegex = /^(http|https):\/\/[^ "]+$/;
        const isInvalid = val && !urlRegex.test(val);
        return (
          <Input 
              value={val} 
              status={isInvalid ? 'error' : undefined}
              onChange={e => handleRowChange(r.key, 'product_url', e.target.value)} 
            />
        );
      }
    },
    {
      title: '가격비교 URL',
      dataIndex: 'compare_url',
      width: 250,
      render: (val, r) => {
        const urlRegex = /^(http|https):\/\/[^ "]+$/;
        const isInvalid = val && !urlRegex.test(val);
        return (
          <Input 
              value={val} 
              status={isInvalid ? 'error' : undefined}
              onChange={e => handleRowChange(r.key, 'compare_url', e.target.value)} 
            />
        );
      }
    },
    {
      title: '시작일',
      dataIndex: 'start_date',
      width: 150,
      render: (val, r) => (
        <DatePicker
          value={val ? dayjs(val) : undefined}
          disabled={!canEditDates}
          disabledDate={(current) => {
            if (canEditDates) return false;
            return !!current && current.startOf('day').isBefore(dayjs().add(1, 'day').startOf('day'));
          }}
          onChange={d => handleRowChange(r.key, 'start_date', d ? d.format('YYYY-MM-DD') : '')}
        />
      )
    },
    {
      title: '종료일',
      dataIndex: 'end_date',
      width: 150,
      render: (val, r) => (
        <DatePicker
          value={val ? dayjs(val) : undefined}
          disabled={!canEditDates}
          disabledDate={(current) => {
            if (canEditDates) return false;
            const min = r.start_date ? dayjs(r.start_date).startOf('day') : dayjs().add(1, 'day').startOf('day');
            return !!current && current.startOf('day').isBefore(min);
          }}
          onChange={d => handleRowChange(r.key, 'end_date', d ? d.format('YYYY-MM-DD') : '')}
        />
      )
    },
    {
      title: '메모',
      dataIndex: 'memo',
      width: 200,
      render: (val, r) => (
        <Input value={val} onChange={e => handleRowChange(r.key, 'memo', e.target.value)} />
      )
    },
  ];

  return (
    <Modal
      title="엑셀 일괄 수정"
      open={open}
      onCancel={onClose}
      width={1200}
      footer={[
        <Button key="revalidate" onClick={() => validateSlotIds(rows)} loading={isValidating}>
          재검증
        </Button>,
        <Button key="cancel" onClick={onClose}>
          취소
        </Button>,
        <Button key="submit" type="primary" disabled={!canSubmit} onClick={handleSubmit} loading={isSubmitting}>
          일괄 수정
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Paragraph>
          업로드된 엑셀 내용을 확인하고 수정하세요. 슬롯 번호는 존재 여부 및 권한 확인 후 수정이 불가능해집니다.
          오류가 있는 경우 슬롯 번호를 수정하여 다시 검증하세요.
        </Paragraph>

        {parseError && <Alert type="error" message={parseError} />}
        
        {isValidating && <Alert icon={<Spin size="small" />} message="슬롯 번호 유효성을 검사 중입니다..." type="info" />}

        <Space wrap size="small" style={{ width: '100%' }}>
          <Text strong>셀러 일괄 적용:</Text>
          <Select
            value={bulkSellerId}
            style={{ width: 260 }}
            placeholder="셀러 선택 (전체 행에 적용)"
            allowClear
            showSearch
            optionFilterProp="label"
            optionLabelProp="label"
            onChange={(val?: number) => {
              setBulkSellerId(val);
              setRows(prev => prev.map(r => ({ ...r, sellerId: val })));
            }}
          >
            {sellers.map(s => (
              <Option key={s.id} value={s.id} label={`${s.username} (${s.name})`}>
                {s.username} ({s.name})
              </Option>
            ))}
          </Select>
          <Button
              onClick={() => {
                setBulkSellerId(undefined);
                setRows(prev => prev.map(r => ({ ...r, sellerId: undefined })));
              }}
            >
              전체 셀러 비우기
            </Button>
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        <Table
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 1500, y: 500 }}
          rowClassName={r => (r.validationError || getRowErrors(r).length > 0) ? 'row-invalid' : ''}
        />
      </Space>
    </Modal>
  );
};

export default ExcelBatchUpdateModal;
